import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Headset, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_API_URL;

const SupportTicket = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: user?.full_name || user?.name || '',
    email: user?.email || '',
    category: 'Select Category',
    subject: '',
    message: '',
  });
  const [ticketNumber, setTicketNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const contactSummary = useMemo(() => ([
    {
      icon: <Mail className="w-5 h-5 text-[#d4a574]" />,
      label: 'Email',
      value: 'support@wearoclub.com',
    },
    {
      icon: <Phone className="w-5 h-5 text-[#d4a574]" />,
      label: 'Phone',
      value: '+91 99999 99999',
    },
  ]), []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.category === 'Select Category') {
      toast.error('Select a valid Category');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/support-tickets`,
        formData,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      const createdTicketNumber = response.data?.ticket_number || '';
      setTicketNumber(createdTicketNumber);
      toast.success(createdTicketNumber ? `Support ticket ${createdTicketNumber} created` : 'Support ticket created');

      setFormData((prev) => ({
        ...prev,
        subject: '',
        message: '',
        category: 'Select Category',
      }));
      navigate('/dashboard?tab=support');
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-6xl mx-auto px-4 py-24">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#d4a574] mb-8 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8">
          <div className="glass-card p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#d4a574]/10 flex items-center justify-center">
                <Headset className="w-6 h-6 text-[#d4a574]" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold gradient-text">Contact Us</h1>
                <p className="text-gray-400 mt-1">Create a support ticket and our team can follow up with you.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="support-name" className="text-gray-300 mb-2 block">Name *</Label>
                  <Input
                    id="support-name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="support-email" className="text-gray-300 mb-2 block">Email *</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="support-category" className="text-gray-300 mb-2 block">Category</Label>
                <select
                  id="support-category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                >
                  <option>Select Category</option>
                  <option>General Question</option>
                  <option>Designer Inquiry</option>
                  <option>Booking Issue</option>
                  <option>Order Update</option>
                  <option>Payment Support</option>
                  <option>Other Query</option>
                </select>
              </div>

              <div>
                <Label htmlFor="support-subject" className="text-gray-300 mb-2 block">Subject *</Label>
                <Input
                  id="support-subject"
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  placeholder="Briefly describe the issue"
                />
              </div>

              <div>
                <Label htmlFor="support-message" className="text-gray-300 mb-2 block">Message *</Label>
                <Textarea
                  id="support-message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="bg-[#0f0f0f] border-[#d4a574]/20 text-white min-h-[160px]"
                  placeholder="Tell us what happened and how we can help."
                />
              </div>

              <button
                type="submit"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                {submitting ? 'Generating...' : 'Generate Support Ticket'}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-semibold text-[#f0d9b5] mb-5">Support Details</h2>
              <div className="space-y-4">
                {contactSummary.map((item) => (
                  <div key={item.label} className="flex items-center gap-3 text-gray-300">
                    {item.icon}
                    <div>
                      <p className="text-sm text-gray-500">{item.label}</p>
                      <p>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="text-2xl font-semibold text-[#f0d9b5] mb-4">What Happens Next</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Submit your issue with the right category and our team can review it faster. Keep your ticket number handy for follow-up.
              </p>
              {ticketNumber ? (
                <div className="rounded-xl border border-[#d4a574]/30 bg-[#d4a574]/10 p-4">
                  <p className="text-sm text-gray-400 mb-1">Latest ticket</p>
                  <p className="text-2xl font-bold text-[#f0d9b5]">{ticketNumber}</p>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-[#d4a574]/20 p-4 text-gray-500">
                  Your generated ticket number will appear here after submission.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicket;
